import { useState, useContext, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import Lottie from 'lottie-react';
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { HeadingTitle } from '../../../components/modules/ui';
import CustomButton from '../../../components/modules/ui/CustomButton';
import FilterControl from '../../../components/modules/table/FilterControl';
import {
  currentMonth,
  currentYear,
  monthsOptions as months,
  yearsOptions
} from '../../../components/utils/constant'
import GlobalContext from '../../../context/global-context';
import ExportToXlsxModal from '../../../components/modules/modals/ExportToXlsxModal';
import businessApi from '../../../api/business.api';
import TableMovimientos from './Table';
import ObligationDueControls from '../ObligationDueControls';
import { isDueStatus, matchesDueFilters } from '../obligationDueFilters';
import { useInfiniteMovements } from '../../../hooks/useInfiniteMovements';
import { useInfiniteComprobantes } from '../../../hooks/useInfiniteComprobantes';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { getDateRange } from '../../../utils/date.util';

export default function ListPorCobrar() {
  const { currentBusiness } = useContext(GlobalContext);

  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [filterValues, setFilterValues] = useState({
    desde: '',
    hasta: '',
    descripcion: ''
  });
  const [typeFilter, setTypeFilter] = useState('');
  const [dueFilters, setDueFilters] = useState({ from: '', to: '', period: '' });
  const hasDueFilters = isDueStatus(typeFilter) || Boolean(dueFilters.from || dueFilters.to || dueFilters.period);
  const documentStatusFilter = isDueStatus(typeFilter) ? undefined : typeFilter || undefined;
  const [showFilters, setShowFilters] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [debouncedDescripcion, setDebouncedDescripcion] = useState('');
  const [debouncedDesde, setDebouncedDesde] = useState('');
  const [debouncedHasta, setDebouncedHasta] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDescripcion(filterValues.descripcion);
    }, 300);

    return () => clearTimeout(timer);
  }, [filterValues.descripcion]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDesde(filterValues.desde);
    }, 300);

    return () => clearTimeout(timer);
  }, [filterValues.desde]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHasta(filterValues.hasta);
    }, 300);

    return () => clearTimeout(timer);
  }, [filterValues.hasta]);

  const {
    data: movementsData,
    fetchNextPage: fetchNextMovements,
    hasNextPage: hasNextMovements,
    isFetchingNextPage: isFetchingNextMovements,
    isLoading: isLoadingMovements,
    isError: isErrorMovements,
    refetch: refetchMovements
  } = useInfiniteMovements({
    businessId: currentBusiness?.id || 0,
    month: monthFilter,
    year: yearFilter,
    linkage: false,
    limit: 30,
    filters: {
      type: 'ingreso',
      documentStatus: documentStatusFilter,
      accountId: 54,
      searchQuery: debouncedDescripcion,
      minAmount: debouncedDesde,
      maxAmount: debouncedHasta
    }
  });

  const dateRange = useMemo(() => getDateRange(yearFilter, monthFilter), [monthFilter, yearFilter]);

  const {
    data: comprobantesData,
    fetchNextPage: fetchNextComprobantes,
    hasNextPage: hasNextComprobantes,
    isFetchingNextPage: isFetchingNextComprobantes,
    isLoading: isLoadingComprobantes,
    isError: isErrorComprobantes,
    refetch: refetchComprobantes
  } = useInfiniteComprobantes({
    businessId: currentBusiness?.id || 0,
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
    limit: 30,
    filters: {
      type: 'aprobados',
      status: documentStatusFilter,
      accountId: 54,
      onlyCreated: true
    }
  });

  // Combinar datos de ambos hooks
  const allData = useMemo(() => {
    const movements = movementsData?.pages?.flatMap(page => page.data) || [];
    const comprobantes = comprobantesData?.pages?.flatMap(page => page.data) || [];
    
    const processedComprobantes = comprobantes.flatMap(comp => {
      if (!comp.listaAuxiliar || comp.listaAuxiliar.length === 0) return [];
      
      return comp.listaAuxiliar
        .filter(item => item.debe > 0)
        .map(aux => ({
          id: `${comp.id}-${aux.libroContableOriginTableId}`,
          fecha: comp.fecha,
          fechaVencimiento: aux.fechaVencimiento || aux.vencimiento || null,
          montoTotal: aux.debe,
          status: 'POR COBRAR',
          nombreFolio: aux.tipoDocumento,
          numeroFolio: aux.folio,
          rut: aux.rut,
          razonSocial: aux.razonSocial,
          linkage: []
        }));
    });

    return [...movements, ...processedComprobantes];
  }, [movementsData, comprobantesData]);

  const isLoading = isLoadingMovements || isLoadingComprobantes;
  const hasLoadError = isErrorMovements || isErrorComprobantes;
  const hasNextPage = hasNextMovements || hasNextComprobantes;
  const isFetchingNextPage = isFetchingNextMovements || isFetchingNextComprobantes;

  const fetchNextPage = () => {
    if (hasNextMovements && !isFetchingNextMovements) fetchNextMovements();
    if (hasNextComprobantes && !isFetchingNextComprobantes) fetchNextComprobantes();
  };

  const refetch = () => {
    refetchMovements();
    refetchComprobantes();
  };

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage: !hasLoadError && hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  });

  const estadoOptions = [
    { value: '', label: 'Todos' },
    { value: 'VENCIDOS', label: 'Vencidos' },
    { value: 'POR VENCER', label: 'Por vencer' },
    { value: 'POR COBRAR', label: 'Por cobrar' },
    { value: 'PARCIALMENTE COBRADO', label: 'Parcialmente cobrado' }
  ];


  const handleFilterChange = (key, value) =>
    setFilterValues(f => ({ ...f, [key]: value }));
  const handleClearFilters = () => {
    setFilterValues({ desde: '', hasta: '', descripcion: '' });
    setMonthFilter('');
    setYearFilter(currentYear);
    setTypeFilter('');
    setDueFilters({ from: '', to: '', period: '' });
    setShowFilters(false);
  };

  const handleExport = async (email) => {
    setIsExporting(true);
    try {
      await businessApi.exportDocumentsReport({
        organizationId: currentBusiness.organizationId,
        businessId: currentBusiness.id,
        email,
        from: dateRange.from,
        to: dateRange.to,
        linkage: false,
        type: 'ingreso',
        accountId: 54
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const filteredData = useMemo(() => {
    let result = allData.map(mov => ({
      ...mov,
      statusStr: String(mov.status).toUpperCase()
    }));

    return Array.from(new Map(
      result.filter(row => matchesDueFilters(row, { ...dueFilters, status: typeFilter, type: 'ingreso' })).map(item => [item.id, item])
    ).values());
  }, [allData, dueFilters, typeFilter]);

  const monthsOptions = useMemo(() => [
    { value: '', label: 'Todos los meses' },
    ...months.map(opt => ({
      ...opt,
      disabled: yearFilter === currentYear && opt.value > currentMonth
    }))
  ], [yearFilter, currentYear, currentMonth])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <HeadingTitle
        title="Resumen de Obligaciones por Cobrar"
        rightIcons={[
          <button
            key='export-xlsx'
            className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
            onClick={() => setIsExportModalOpen(true)}
          >
            XLS
          </button>
        ]}
        iconHeight={32}
      />

      <div className="mb-4">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            <FilterControl
              label="Mes"
              value={monthFilter}
              onChange={setMonthFilter}
              options={monthsOptions}
              fullWidth
            />
            <FilterControl
              label="Año"
              value={yearFilter}
              onChange={setYearFilter}
              options={yearsOptions}
              fullWidth
            />
            <FilterControl
              label="Estado"
              value={typeFilter}
              onChange={setTypeFilter}
              options={estadoOptions}
              fullWidth
            />
            <CustomButton
              className="rounded-full h-8 w-full"
              color="primary"
              title="Buscar"
              onClick={refetch}
              disabled={isLoading}
            />
            <CustomButton
              className="rounded-full h-8 w-full"
              color="accent1"
              title="Limpiar filtros"
              onClick={handleClearFilters}
              disabled={isLoading}
            />
          </div>
          <div className="flex justify-end">
            <button
              className="flex items-center px-2 py-1 bg-surface/70 border border-border rounded hover:bg-border/30 transition"
              onClick={() => setShowFilters(prev => !prev)}
            >
              <KeyboardArrowDownIcon
                className={clsx('transition-transform', {
                  'rotate-180': showFilters
                })}
              />
            </button>
          </div>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 items-end gap-x-2 gap-y-4 border border-border rounded-md p-3 bg-surface/70 mt-4">
            <ObligationDueControls value={dueFilters} onChange={setDueFilters} />

            <div className="flex flex-col min-w-0 w-full">
              <label className="text-xs font-sans font-medium text-gray-500 leading-none mb-0 translate-y-[2px] z-10">Monto Desde </label>
              <input
                type="number"
                placeholder="Desde"
                value={filterValues.desde}
                onChange={e => handleFilterChange('desde', e.target.value)}
                className="w-full min-w-0 h-8 border border-border rounded-full px-2 py-1 text-sm font-sans font-normal focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            <div className="flex flex-col min-w-0 w-full">
              <label className="text-xs font-sans font-medium text-gray-500 leading-none mb-0 translate-y-[2px] z-10">
                Monto Hasta
              </label>
              <input
                type="number"
                placeholder="Hasta"
                value={filterValues.hasta}
                onChange={e => handleFilterChange('hasta', e.target.value)}
                className="w-full min-w-0 h-8 border border-border rounded-full px-2 py-1 text-sm font-sans font-normal focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            <div className="flex flex-col min-w-0 w-full">
              <label className="text-xs font-sans font-medium text-gray-500 leading-none mb-0 translate-y-[2px] z-10">
                Buscar Cliente/RUT
              </label>
              <input
                type="text"
                placeholder="Descripción"
                value={filterValues.descripcion}
                onChange={e => handleFilterChange('descripcion', e.target.value)}
                className="w-full min-w-0 h-8 border border-border rounded-full px-2 py-1 text-sm font-sans font-normal focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>
        )}
      </div>

      {hasDueFilters && hasLoadError ? (
        <div className="py-12 text-center text-danger" role="alert">No se pudieron cargar todos los documentos para aplicar los filtros. Presiona Buscar para reintentar.</div>
      ) : isLoading ? (
        <div className='flex min-h-0 flex-1 items-center justify-center pt-8'>
          <Lottie animationData={LoaderAdm} loop style={{ width: 300, height: 300 }} />
        </div>
      ) : filteredData.length === 0 ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {hasNextPage ? (
            <div ref={lastElementRef} className="flex justify-center py-4" role="status" aria-label="Buscando documentos">
              <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
            </div>
          ) : (
            <div className="py-12 text-center text-muted font-sans">No hay datos para mostrar con estos filtros</div>
          )}
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <TableMovimientos
            data={filteredData}
            lastElementRef={lastElementRef}
            className="h-full min-h-[260px] border border-border rounded bg-surface font-sans text-text"
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </div>
      )}
      {hasDueFilters && hasNextPage && !isLoading && !hasLoadError && (
        <p className="shrink-0 py-2 text-xs font-sans text-muted" aria-live="polite">Resultados y totales parciales mientras se cargan los documentos.</p>
      )}
      <ExportToXlsxModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title='Exportar Resumen por Cobrar'
        description='Se enviará un archivo Excel con el resumen de obligaciones por cobrar al email especificado'
        isLoading={isExporting}
      />
    </div>
  );
}
